<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">المدونة</h1>
          <p class="ds-page-header__subtitle">إدارة مقالات المدونة وأقسامها</p>
        </div>
        <div class="ds-page-header__actions">
          <router-link to="/blog/categories" class="ds-btn ds-btn--secondary">
            <i class="pi pi-tags"></i> الأقسام
          </router-link>
          <router-link to="/blog/add" class="ds-btn ds-btn--primary">
            <i class="pi pi-plus"></i> إضافة مقال
          </router-link>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter">
        <div class="filter__header">
          <div class="filter__header-icon"><i class="pi pi-filter"></i></div>
          <p class="filter__title">عوامل التصفية</p>
        </div>
        <div class="ds-field-grid ds-field-grid--4">
          <div class="form-field">
            <label class="field-label">القسم</label>
            <select class="form-select" v-model="store.filters.category_id" @change="reload">
              <option value="">الكل</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.title }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">الحالة</label>
            <select class="form-select" v-model="store.filters.is_published" @change="reload">
              <option value="">الكل</option>
              <option value="1">منشور</option>
              <option value="0">مسودة</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">مميز</label>
            <select class="form-select" v-model="store.filters.is_featured" @change="reload">
              <option value="">الكل</option>
              <option value="1">مميز</option>
              <option value="0">عادي</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">بحث بالعنوان</label>
            <div class="d-flex gap-2">
              <input type="text" class="form-control" v-model="store.filters.search" @keyup.enter="reload" placeholder="ابحث في العناوين..." />
              <button class="ds-btn ds-btn--secondary" style="padding:0.5625rem 0.875rem" v-tooltip.top="'إعادة تعيين'" @click="resetFilters">
                <i class="pi pi-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <Loading v-if="store.loading" />

      <template v-else>
        <div v-if="!store.posts.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-inbox"></i></div>
          <h3 class="empty-state__title">لا توجد مقالات</h3>
          <p class="empty-state__desc">لا توجد مقالات مطابقة لعوامل التصفية الحالية.</p>
          <router-link to="/blog/add" class="ds-btn ds-btn--primary empty-state__action">
            <i class="pi pi-plus"></i> إضافة مقال
          </router-link>
        </div>

        <template v-else>
          <!-- Desktop / tablet table -->
          <div class="ds-table-wrap table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th class="ps-3">المقال</th>
                  <th>القسم</th>
                  <th class="text-center">الحالة</th>
                  <th class="text-center">مميز</th>
                  <th>تاريخ النشر</th>
                  <th class="text-center pe-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="post in store.posts" :key="post.id">
                  <td class="ps-3">
                    <div class="post-cell">
                      <img v-if="post.featured_image_link" :src="post.featured_image_link" class="post-thumb" alt="" />
                      <span v-else class="post-thumb post-thumb--empty"><i class="pi pi-file-edit"></i></span>
                      <div class="post-name">{{ post.title }}</div>
                    </div>
                  </td>
                  <td>{{ post.category?.title || '—' }}</td>
                  <td class="text-center">
                    <button
                      class="chip-toggle" :class="post.is_published ? 'chip-toggle--on' : 'chip-toggle--off'"
                      v-tooltip.top="post.is_published ? 'اضغط لإخفاء (تحويل لمسودة)' : 'اضغط للنشر'"
                      @click="togglePublish(post)"
                    >
                      <i :class="post.is_published ? 'pi pi-eye' : 'pi pi-eye-slash'"></i> {{ post.is_published ? 'منشور' : 'مسودة' }}
                    </button>
                  </td>
                  <td class="text-center">
                    <button
                      class="chip-toggle" :class="post.is_featured ? 'chip-toggle--home' : 'chip-toggle--off'"
                      v-tooltip.top="'تمييز المقال في الصفحة الرئيسية بالمدونة'"
                      @click="toggleFeatured(post)"
                    >
                      <i class="pi pi-star-fill"></i> {{ post.is_featured ? 'مميز' : '—' }}
                    </button>
                  </td>
                  <td><small>{{ date(post.published_at) }}</small></td>
                  <td class="text-center pe-3">
                    <div class="icon-action-group justify-content-center">
                      <router-link :to="`/blog/${post.id}`" class="icon-action-btn icon-action-btn--neutral" v-tooltip.top="'عرض'">
                        <i class="pi pi-eye"></i>
                      </router-link>
                      <router-link :to="`/blog/edit/${post.id}`" class="icon-action-btn icon-action-btn--edit" v-tooltip.top="'تعديل'">
                        <i class="pi pi-pencil"></i>
                      </router-link>
                      <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف'" @click="remove(post)">
                        <i class="pi pi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile card list -->
          <div class="mobile-list">
            <div v-for="post in store.posts" :key="post.id" class="mobile-list-card">
              <div class="mobile-list-card__top">
                <img v-if="post.featured_image_link" :src="post.featured_image_link" class="mobile-list-card__thumb" alt="" />
                <span v-else class="mobile-list-card__thumb"><i class="pi pi-file-edit"></i></span>
                <div class="mobile-list-card__meta">
                  <div class="mobile-list-card__title">{{ post.title }}</div>
                  <span v-if="post.category" class="badge-pill">{{ post.category.title }}</span>
                </div>
                <span class="status-badge" :class="post.is_published ? 'status-badge--accepted' : 'status-badge--pending'">
                  {{ post.is_published ? 'منشور' : 'مسودة' }}
                </span>
              </div>
              <div class="mobile-list-card__actions">
                <router-link :to="`/blog/${post.id}`" class="ds-btn ds-btn--secondary ds-btn--sm"><i class="pi pi-eye"></i> عرض</router-link>
                <router-link :to="`/blog/edit/${post.id}`" class="ds-btn ds-btn--secondary ds-btn--sm"><i class="pi pi-pencil"></i></router-link>
                <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="remove(post)"><i class="pi pi-trash"></i></button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3" v-if="store.pagination && store.pagination.last_page > 1">
            <pagination :data="store.pagination" @pagination-change-page="store.fetchPosts" class="pagination mb-0" />
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useBlogStore } from '@/stores/useBlogStore.js';

export default {
  name: 'BlogIndex',
  components: { Breadcrumb, Loading, pagination },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'المدونة', path: '/blog' }],
      categories: [],
    };
  },
  computed: {
    store() { return useBlogStore(); },
  },
  methods: {
    date(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' });
    },
    reload() { this.store.fetchPosts(1); },
    resetFilters() {
      this.store.filters.category_id = '';
      this.store.filters.is_published = '';
      this.store.filters.is_featured = '';
      this.store.filters.search = '';
      this.store.fetchPosts(1);
    },
    async fetchCategories() {
      try {
        const { data } = await axios.get('/admin/blog-categories');
        this.categories = data.data ?? [];
      } catch { /* non-fatal */ }
    },
    async togglePublish(post) {
      try { await this.store.togglePublish(post.id); }
      catch (e) { Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2000, showConfirmButton: false }); }
    },
    async toggleFeatured(post) {
      try { await this.store.toggleFeatured(post.id); }
      catch (e) { Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2000, showConfirmButton: false }); }
    },
    async remove(post) {
      const res = await Swal.fire({
        title: 'حذف المقال', html: `سيتم نقل <strong>${post.title}</strong> إلى سلة المحذوفات ويمكن استرجاعه لاحقاً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deletePost(post.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: e.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchPosts(1);
    this.fetchCategories();
  },
};
</script>

<style scoped>
.post-cell { display: flex; align-items: center; gap: 0.75rem; }
.post-thumb {
  width: 44px; height: 44px; border-radius: var(--radius-md); object-fit: cover; display: inline-flex;
  align-items: center; justify-content: center; background: var(--primary-light); color: var(--primary-color);
  border: 1px solid var(--surface-border); font-size: 1.0625rem; flex-shrink: 0;
}
.post-name { font-weight: 700; color: var(--text-primary); font-size: 0.875rem; }
</style>
